import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Optional,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';

import { NgIf, NgTemplateOutlet } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { SortArrowDirection } from '../../utils/sort-direction';

/**
 * A sortable table header's control: a real `<button>` around whatever the header already renders.
 *
 * WHY A BUTTON AND NOT `role`+`tabindex`+a keydown handler: the platform supplies the tab stop, the
 * Enter/Space activation and the role, and `styles.scss`'s global `:focus-visible` ring already
 * covers `<button>` — so there is no keyboard code here to get wrong, and no focus work.
 *
 * WHY THE CONTENT IS PROJECTED AND NOT RENDERED: the two table families draw their indicator
 * differently (a text `▲`/`▼` glyph in ingest, a `<mat-icon>` plus a `matBadge` in the core table).
 * Rendering them as one thing would move pixels, which this change promises not to do, so this
 * component takes them as content and leaves them byte-identical.
 *
 * WHY `aria-sort` IS WRITTEN ON THE PARENT: ARIA permits it only on a `columnheader`/`rowheader`.
 * `mat-header-cell` carries `role="columnheader"` from CDK (`@angular/cdk/fesm2020/table.mjs:228`)
 * and `table-edit-rows` uses real `<th scope="col">`, which carries it implicitly — so the
 * attribute belongs on the cell and the control belongs inside it, which is the W3C APG sortable
 * table pattern. Putting `role="button"` on the cell instead would OVERWRITE `columnheader`, and a
 * screen-reader user would stop being told "column 3 of 7": a semantic loss, not a style question.
 * Writing one node outward is deliberate. The alternative is a second `[attr.aria-sort]` binding at
 * each of 56 call sites (the sweep pins the count), any one of which can be forgotten silently.
 */
@Component({
  selector: 'app-sort-header',
  standalone: true,
  imports: [NgIf, NgTemplateOutlet, TranslateModule],
  templateUrl: './sort-header.component.html',
  styleUrls: ['./sort-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SortHeaderComponent implements OnInit, OnChanges, AfterViewInit, AfterViewChecked, OnDestroy {
  /** Which way this column is sorted; `null` is unsorted. */
  @Input() direction: SortArrowDirection = null;

  /**
   * Whether this column can be sorted at all. The core table decides per column
   * (`columnField.sortable`) and `table-edit-rows` per table (`noPaginationAndSort`), so one
   * template serves both states and this cannot be a call-site choice.
   */
  @Input() sortable = true;

  /**
   * On the HOST, which lives in the calling component's view: a parent stylesheet can then ask
   * "does this row hold a sort control" (`:has(app-sort-header.sort-header--sortable)`) under its
   * own encapsulation. The bare branch renders no control, and must not count as one.
   */
  @HostBinding('class.sort-header--sortable') get sortableClass(): boolean {
    return this.sortable;
  }

  /**
   * This column's rank as a SECONDARY sort (2, 3, ...), or `null` for the primary sort and an
   * unsorted column. `aria-sort` may name one header only, so a secondary sort is spoken by text
   * this component renders BESIDE its button — inside it, the rank would become part of the
   * button's name, which must stay the column label. It used to be hand-written at five call
   * sites; one copy here is what keeps "outside the button" structural.
   */
  @Input() rank: number | null = null;

  /** The secondary sort's direction; read only while `rank` is set. */
  @Input() rankDirection: SortArrowDirection = null;

  @Output() sort = new EventEmitter<void>();

  private writtenOn: HTMLElement | null = null;

  /** The name check is owed, and waits for the language file (see `ngAfterViewInit`). */
  private nameCheckPending = false;
  private translationsReady = false;
  private translationsSub?: Subscription;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    // Optional: the app always has one, but a host that renders this without a translate provider
    // (several table specs do, since the rank text is the only pipe here) must still build.
    @Optional() private readonly translate: TranslateService | null,
  ) {}

  /** Also written here: a call site binding no input never triggers `ngOnChanges`. */
  ngOnInit(): void {
    this.writeAriaSort();
  }

  ngOnChanges(): void {
    this.writeAriaSort();
  }

  /**
   * Once, not per change-detection pass: 56 headers must not walk their subtree on every tick. But
   * not BEFORE the language file: labels arrive through `| translate`, which renders '' until it
   * loads, and `AppLocaleService.init()` does not wait for it — so a table painted first would warn
   * about every correctly named header. `translate.get` emits once the file is in (synchronously
   * when it already is), and the check then runs on the next view check, after the pipes repaint.
   */
  ngAfterViewInit(): void {
    this.nameCheckPending = true;
    if (!this.translate) {
      this.translationsReady = true;
    }
    this.translationsSub = this.translate
      ?.get('common.table.secondarySortAsc')
      .pipe(take(1))
      .subscribe({
        next: () => (this.translationsReady = true),
        // No file at all is not a reason to stay silent: check what did render.
        error: () => (this.translationsReady = true),
      });
    this.runPendingNameCheck();
  }

  ngAfterViewChecked(): void {
    this.runPendingNameCheck();
  }

  /**
   * This component writes `aria-sort` onto a node it does not own, so it takes it back: a cell
   * that outlives its control must not keep announcing a sort for a column with no sort control.
   */
  ngOnDestroy(): void {
    this.translationsSub?.unsubscribe();
    // The host may already be detached from the DOM here, so use the cell remembered at write time.
    if (this.writtenOn) {
      this.renderer.removeAttribute(this.writtenOn, 'aria-sort');
    }
  }

  activate(): void {
    if (this.sortable) {
      this.sort.emit();
    }
  }

  /**
   * KEY AUTOREPEAT IS NOT A SEQUENCE OF PRESSES, and this is the one input the keyboard brings
   * that a pointer cannot. A held Enter fires `click` every few tens of milliseconds; on
   * the core table, whose cycle has three states, that walks ASC -> DESC -> removed and emits a
   * search per step.
   *
   * `preventDefault()` on the REPEATED keydown is what stops the synthetic click, which is exact
   * where a time window would be a guess: `event.repeat` is the platform telling us the key never
   * came up. The first press is untouched, and a user pressing Enter twice deliberately releases
   * the key in between, so `repeat` is false both times.
   *
   * ENTER ONLY. A button activates on Space at KEYUP, so a held Space clicks once however long it
   * is held (measured in Chromium: one click either way), and cancelling its repeated keydowns
   * bought nothing while risking the press itself in an engine that ties keyup activation to an
   * uncancelled keydown.
   *
   * It lives here rather than in each of the five sort handlers so all 56 headers get it once.
   *
   * It is bound on the BUTTON in the template, not as a host listener: the button is the narrower
   * target, and the two are identical for real (bubbling) keydowns. Do not "restore" a
   * HostListener.
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.repeat && event.key === 'Enter') {
      event.preventDefault();
    }
  }

  /**
   * `aria-sort` on the nearest columnheader ABOVE this control, or nowhere.
   *
   * A wrong `aria-sort` is worse than none: on a `<div>` that is not a columnheader it is invalid
   * and ignored by some screen readers and announced by others, so a caller that forgets the cell
   * gets silence rather than a plausible lie.
   *
   * A column that cannot be sorted has the attribute REMOVED rather than set to `none`: `none`
   * means "sortable, not currently sorted", which is a different claim. So does a SECONDARY sort:
   * it IS sorted, so `none` would contradict the rank text beside it, and ARIA lets only one header
   * carry a direction — the attribute is absent and the text says the rest.
   */
  private writeAriaSort(): void {
    const header = this.columnHeaderAncestor();
    if (!header) {
      return;
    }
    this.writtenOn = header;
    if (!this.sortable || this.rank !== null) {
      this.renderer.removeAttribute(header, 'aria-sort');
      return;
    }
    const value =
      this.direction === 'asc' ? 'ascending' : this.direction === 'desc' ? 'descending' : 'none';
    this.renderer.setAttribute(header, 'aria-sort', value);
  }

  /**
   * The nearest columnheader above the control. Bounded: it stops at the first row or table
   * boundary rather than climbing to `<html>`, so it can never write to a stranger.
   */
  private columnHeaderAncestor(): HTMLElement | null {
    let node = this.host.nativeElement.parentElement;
    while (node) {
      const role = node.getAttribute('role');
      if (role === 'columnheader' || (node.tagName === 'TH' && role === null)) {
        return node;
      }
      if (role === 'row' || role === 'table' || node.tagName === 'TR' || node.tagName === 'TABLE') {
        return null;
      }
      node = node.parentElement;
    }
    return null;
  }

  /**
   * CONTRACT: the button's accessible name is the projected text that is NOT `aria-hidden`.
   * Every projected indicator (a `<mat-icon>` ligature, a badge digit, a `▲`) must carry
   * `aria-hidden="true"`, so the name is the column label alone and state lives in `aria-sort`.
   * An empty label is then a NAMELESS button; this component cannot invent the name, but it must
   * not hide the problem, and no static gate can see it: the label arrives through `| translate`
   * at runtime. Checked once, after the first render that follows the language file's arrival.
   */
  private runPendingNameCheck(): void {
    if (this.nameCheckPending && this.translationsReady) {
      this.nameCheckPending = false;
      this.warnIfUnnamed();
    }
  }

  private warnIfUnnamed(): void {
    if (this.sortable && !this.accessibleText(this.host.nativeElement).trim()) {
      console.warn(
        '[app-sort-header] a sortable header rendered no text, so its button has no accessible ' +
          'name. Give the column a label, or mark the header unsortable.',
      );
    }
  }

  private accessibleText(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent ?? '';
    }
    if (node.nodeType !== Node.ELEMENT_NODE || (node as Element).getAttribute('aria-hidden') === 'true') {
      return '';
    }
    return Array.from(node.childNodes).map((c) => this.accessibleText(c)).join('');
  }
}
